import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  Divider
} from '@mui/material';
import { useWizard } from '../../context/WizardContext';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const BasicInfoStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  useProgressTracking({ stepName: 'About You', stepNumber: 1 });
  const { personal } = wizardData;

  const [ageError, setAgeError] = useState('');
  const [partnerAgeError, setPartnerAgeError] = useState('');

  const validateAge = (age: number): string => {
    if (isNaN(age)) return '';
    if (age < 18) return 'Must be at least 18 years old';
    if (age > 100) return 'Please enter a valid age';
    return '';
  };

  const getAgeFromDob = (dob: string): number | '' => {
    if (!dob) return '';
    const currentYear = new Date().getFullYear();
    const birthYear = parseInt(dob.substring(0, 4), 10);
    if (isNaN(birthYear)) return '';
    return currentYear - birthYear;
  };

  const handleAgeChange = (ageStr: string, client: 'client1' | 'client2') => {
    if (ageStr === '') {
      updateWizardData({
        personal: {
          ...personal,
          [client]: { ...personal[client], dateOfBirth: '' }
        }
      });
      if (client === 'client1') setAgeError('');
      else setPartnerAgeError('');
      return;
    }
    const age = parseInt(ageStr, 10);
    if (isNaN(age) || age < 0 || age > 120) return;
    const currentYear = new Date().getFullYear();
    const dob = `${currentYear - age}-01-01`;
    updateWizardData({
      personal: {
        ...personal,
        [client]: { ...personal[client], dateOfBirth: dob }
      }
    });
    const error = validateAge(age);
    if (client === 'client1') setAgeError(error);
    else setPartnerAgeError(error);
  };

  const handleClient1Change = (field: string, value: string) => {
    updateWizardData({
      personal: {
        ...personal,
        client1: { ...personal.client1, [field]: value }
      }
    });
  };

  const handleClient2Change = (field: string, value: string) => {
    updateWizardData({
      personal: {
        ...personal,
        client2: { ...personal.client2, [field]: value }
      }
    });
  };

  const handlePartnerToggle = (checked: boolean) => {
    updateWizardData({
      personal: {
        ...personal,
        hasPartner: checked,
        client2: checked ? personal.client2 : { firstName: '', lastName: '', dateOfBirth: '' }
      },
      uiState: { ...wizardData.uiState, hasAnsweredPartnerQuestion: true }
    });
    if (!checked) setPartnerAgeError('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" fontWeight="bold" textAlign="center">
        Tell Us About Yourself
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Just a few quick details to get started
      </Typography>

      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name"
              value={personal.client1.firstName}
              onChange={(e) => handleClient1Change('firstName', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={personal.client1.lastName}
              onChange={(e) => handleClient1Change('lastName', e.target.value)}
              fullWidth
              required
            />
          </Box>

          <TextField
            label="Age"
            type="number"
            value={getAgeFromDob(personal.client1.dateOfBirth)}
            onChange={(e) => handleAgeChange(e.target.value, 'client1')}
            inputProps={{ min: 18, max: 100 }}
            error={!!ageError}
            helperText={ageError}
            fullWidth
            required
          />

          {/* Partner toggle */}
          <Divider sx={{ my: 0.5 }} />
          <FormControlLabel
            control={
              <Switch
                checked={personal.hasPartner || false}
                onChange={(e) => handlePartnerToggle(e.target.checked)}
              />
            }
            label="Planning with a partner?"
          />

          {personal.hasPartner && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Partner's First Name"
                  value={personal.client2.firstName}
                  onChange={(e) => handleClient2Change('firstName', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Partner's Last Name"
                  value={personal.client2.lastName}
                  onChange={(e) => handleClient2Change('lastName', e.target.value)}
                  fullWidth
                />
              </Box>
              <TextField
                label="Partner's Age"
                type="number"
                value={getAgeFromDob(personal.client2.dateOfBirth)}
                onChange={(e) => handleAgeChange(e.target.value, 'client2')}
                inputProps={{ min: 18, max: 100 }}
                error={!!partnerAgeError}
                helperText={partnerAgeError}
                fullWidth
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default BasicInfoStep;
